import { createBrowserConfig } from '@cian/config/browser';
import * as React from 'react';
import { render } from 'react-dom';
import { Store } from 'redux';

import PhotoGallery, { IPhotoGalleryProps } from '../common/components/photo_gallery/photo_gallery';
import { makeSmoothScrolling } from '../common/utils/smooth_scroll';

import AnchorList, { IAnchorListProps } from './components/anchor_list/anchor_list';
import ObjectCorps, { IObjectCorpsProps } from './components/object_corps/object_corps';
import initObjectPopupError from './components/object_error_form/index';
import ObjectMapView, { IObjectMapViewProps } from './components/object_map_view/object_map_view';
import BSCDetailPhoneShow, { IBSCDetailPhoneShowProps } from './components/object_phone_show/object_phone_show';
import OfferCardList, { IOfferCardListProps } from './components/offer_card_list/offer_card_list';
import Serp, { ISerpProps } from './components/serp/serp';

import { BSCAddCard } from './components/add_bsc_card/add_bsc_card';

import './legacy/detail.css';

interface IBusinessShoppingCenterDetailBundle {
  AnchorList: typeof AnchorList;
  BSCDetailSerp: typeof Serp;
  OfferCardList: typeof OfferCardList;
  BSCDetailPhoneShow: typeof BSCDetailPhoneShow;
  ObjectMapView: typeof ObjectMapView;
  PhotoGallery: typeof PhotoGallery;
  ObjectCorps: typeof ObjectCorps;
  BSCAddCard: typeof BSCAddCard;

  makeSmoothScrolling: typeof makeSmoothScrolling;
}

function loadBundle(): Promise<IBusinessShoppingCenterDetailBundle> {
  return new Promise((resolve, reject) => {
    require.ensure([
      './components/anchor_list/anchor_list',
      './components/serp/serp',
      './components/offer_card_list/offer_card_list',
      './components/object_phone_show/object_phone_show',
      './components/object_map_view/object_map_view',
      './components/object_corps/object_corps',
      '../common/components/photo_gallery/photo_gallery',
      './components/add_bsc_card/add_bsc_card',

      '../common/utils/smooth_scroll',
    ], () => {
      resolve({
        AnchorList: require('./components/anchor_list/anchor_list').default,
        BSCDetailSerp: require('./components/serp/serp').default,
        OfferCardList: require('./components/offer_card_list/offer_card_list').default,
        BSCDetailPhoneShow: require('./components/object_phone_show/object_phone_show').default,
        ObjectMapView: require('./components/object_map_view/object_map_view').default,
        ObjectCorps: require('./components/object_corps/object_corps').default,
        PhotoGallery: require('../common/components/photo_gallery/photo_gallery').default,
        BSCAddCard: require('./components/add_bsc_card/add_bsc_card').BSCAddCard,

        makeSmoothScrolling: require('../common/utils/smooth_scroll').makeSmoothScrolling,
      } as IBusinessShoppingCenterDetailBundle);
    });
  });
}

function getYmapsUrlWithKey(): string {
  const config = createBrowserConfig({ settingsKey: 'business_shopping_centers-detail-monolith-frontend' });

  const ymapsUrl = config.get<string>('yandex.browser.ymaps_url');
  const ymapsKey = config.get<string>('yandex.browser.ymaps_key');
  const ymapsSuggestKey = config.get<string>('yandex.browser.ymaps_suggest_key');

  return `${ymapsUrl}${ymapsKey}&suggest_apikey=${ymapsSuggestKey}`;
}

export default function initBusinessShoppingCenterDetail<T>(store: Store<T>) {
  /* tslint:disable-next-line:no-any */
  const exports = (window as any);

  exports.makeSmoothScrolling = (anchorElement: HTMLAnchorElement) => (
    loadBundle()
      .then(({ makeSmoothScrolling }) => (
        makeSmoothScrolling(anchorElement)
      ))
  );
  exports.renderAnchorList = (element: Element, props: IAnchorListProps) => (
    loadBundle()
      .then(({ AnchorList }) => (
        render(<AnchorList {...props} />, element)
      ))
  );
  exports.renderBSCDetailSerp = (element: Element, props: ISerpProps) => (
    loadBundle()
      .then(({ BSCDetailSerp }) => {
        let SerpInstance: Serp;
        return new Promise(resolve => {
          render(
            <BSCDetailSerp {...props} ref={ref => SerpInstance = ref} />,
            element,
            () => resolve(SerpInstance)
          );
        });
      })
  );
  exports.renderBSCDetailOfferCardList = (element: Element, props: IOfferCardListProps) => (
    loadBundle()
      .then(({ OfferCardList }) => (
        render(<OfferCardList {...props} />, element)
      ))
  );
  exports.renderBSCDetailObjectPhoneShow = (element: Element, props: IBSCDetailPhoneShowProps) => (
    loadBundle()
      .then(({ BSCDetailPhoneShow }) => (
        render(<BSCDetailPhoneShow {...props} />, element)
      ))
  );
  exports.renderBSCDetailPhotoGallery = (element: Element, props: IPhotoGalleryProps) => (
    loadBundle()
      .then(({ PhotoGallery }) => (
        render(<PhotoGallery {...props} />, element)
      ))
  );

  exports.renderBSCDetailAddCard = (element: Element) => (
    loadBundle()
      .then(({ BSCAddCard }) => (
        render(<BSCAddCard />, element)
      ))
  );

  exports.renderBSCDetailObjectError = initObjectPopupError(store);

  exports.renderBSCDetailObjectMapView = (element: Element, props: IObjectMapViewProps) => {
    loadBundle()
      .then(({ ObjectMapView }) => {
        render(<ObjectMapView ymapsUrlWithKey={getYmapsUrlWithKey()} {...props} />, element);
      });
  };

  exports.renderBSDetailObjectCorps = (element: Element, props: IObjectCorpsProps) => {
    loadBundle()
      .then(({ ObjectCorps }) => {
        render(<ObjectCorps {...props} />, element);
      });
  };
}



// WEBPACK FOOTER //
// ./src/bundles/business_shopping_center_detail/index.tsx